import { createContext, useState, useContext } from "react";
import { userRepository, trackingService } from "../../../di/di";

const ForgotPasswordContext = createContext();

/**
 * Provides a context for managing the forgot password functionality.
 * 
 * This context includes the state and logic necessary for handling
 * the process of sending a password recovery link to a user's email.
 * 
 * State:
 * - email: The email address input by the user.
 * - isProcessing: A boolean indicating if the recovery link request is in progress.
 * - error: An error message if the recovery link request fails.
 * - isSuccess: A boolean indicating if the recovery link was successfully sent.
 * 
 * Methods:
 * - sendRecoveryLink: An async function that attempts to send a recovery link
 *   to the provided email. Updates state based on the success or failure of the operation.
 * 
 * @param {Object} children - The children components that will have access to this context.
 */

export const ForgotPasswordContextProvider = ({ children }) => {
    const [email, setEmail] = useState("");
    const [isProcessing, setIsProcessing] = useState(false);
    const [error, setError] = useState("");
    const [isSuccess, setIsSuccess] = useState(false);

    const sendRecoveryLink = async () => {
        try {
            setError("");
            setIsProcessing(true);
            trackingService.trackEvent("forgot_password_initiated", { email, timestamp: new Date().toISOString() });
            const apiResponse = await userRepository.sendRecoveryLink(email);

            if (!apiResponse.success) {
                setError(apiResponse.message);
                trackingService.trackEvent("forgot_password_failed", { email, failure_reason: apiResponse.message, timestamp: new Date().toISOString() });
                return;
            }
            trackingService.trackEvent("forgot_password_success", { email, timestamp: new Date().toISOString() });
            setIsSuccess(true);
        }
        catch (e) {
            trackingService.trackEvent("forgot_password_failed", { email, failure_reason: e.message, timestamp: new Date().toISOString() });
            if (e.response?.data?.message) setError(e.response.data.message);
        }
        finally {
            setIsProcessing(false);
        }
    }

    const value = {
        email,
        setEmail,
        error,
        setError,
        isSuccess,
        setIsSuccess,
        isProcessing,
        setIsProcessing,
        sendRecoveryLink
    };

    return (
        <ForgotPasswordContext.Provider value={value}>
            {children}
        </ForgotPasswordContext.Provider>
    )
}

export const useForgotPasswordContext = () => useContext(ForgotPasswordContext);